// OP1WebAWButton: the look (skeleton + CSS + `status` variant) lives in
// Plasmic; the window-button behaviour is added here. Same rules as the code
// component OP1WebButtonAppWindow:
//   closed window            -> click opens it
//   open + minimized         -> click restores it
//   open + focused           -> click minimizes it
//   open + not focused       -> click focuses it
//
// The Plasmic `status` variant is driven by the live window state:
//   "closed" | "open" | "active".
// With a `windowId` the live state wins; without one (or outside the Desktop
// provider) the button is inert and shows the `status` set in Studio. The root also gets data-state / data-open /
// data-focused / aria-pressed for CSS or tests.
//
// This file is owned by us: `plasmic sync` never overwrites it.
import * as React from "react";
import { PlasmicOp1WebAwButton } from "./plasmic/one_platform/PlasmicOp1WebAwButton";
import useWindowButton from "./useWindowButton";

function Op1WebAwButton_(
  { windowId, onClick, status, ...props },
  ref
) {
  const { enabled, state, isOpen, isFocused, handleClick } =
    useWindowButton(windowId);

  // With a windowId the live window state drives the variant (closed / open /
  // active). Without one, the `status` set in Studio is shown as designed.
  const finalStatus = enabled ? state : status;

  const composedOnClick =
    enabled || onClick
      ? (...args) => {
          onClick?.(...args);
          handleClick();
        }
      : undefined;

  const windowProps = enabled
    ? {
        "data-state": state,
        "data-open": isOpen,
        "data-focused": isFocused,
        "aria-pressed": state === "active",
      }
    : {};

  return (
    // The generated <button> has no onClick of its own (Plasmic only wires one
    // when the design has an interaction), so the handler is attached to the
    // root node through an override instead of the `onClick` prop.
    <PlasmicOp1WebAwButton
      {...props}
      {...windowProps}
      root={{ ref, onClick: composedOnClick }}
      status={finalStatus}
    />
  );
}

const Op1WebAwButton = React.forwardRef(Op1WebAwButton_);

export default Op1WebAwButton;
